import React, { useCallback } from "react";
import { useRouter } from "next/router";
import { TextStyle, Thumbnail, TopBar } from "@shopify/polaris";
import { ElasticProduct, Results, Stats, useSearch } from "@usereactify/search";

export const SearchInput = () => {
  const searchHook = useSearch();

  const handleChange = React.useCallback(
    (value: string) => {
      searchHook.setSearchTerm(value);
      searchHook.setShowInstantSearchResults(true);
    },
    [searchHook]
  );

  return (
    <TopBar.SearchField
      placeholder="Instant search"
      value={searchHook.searchTerm}
      onChange={handleChange}
      onFocus={() => searchHook.setShowInstantSearchResults(true)}
    />
  );
};

export const SearchResults = () => {
  const searchHook = useSearch();

  const handleViewMore = useCallback(() => {
    searchHook.submitSearchTerm();
  }, [searchHook]);

  return (
    <div
      style={{
        overflow: "scroll",
        maxHeight: "60vh",
      }}
    >
      <Results
        listStyle={{
          display: "grid",
          gap: "5px",
          gridTemplateColumns: "repeat(1, minmax(0, 1fr))",
        }}
        pageSize={5}
        listClassName="Polaris-TopBar-SearchField__Results"
        renderResultCardProduct={SearchResultsItem}
        renderNoResults={() => (
          <span
            className="Polaris-TopBar-SearchField__ViewMore"
            style={{
              paddingTop: "1rem",
            }}
          >
            {"No results"}
          </span>
        )}
      />
      <Stats
        render={(renderProps) =>
          (renderProps.resultStats.numberOfResults ?? 0) > 0 ? (
            <a className="Polaris-TopBar-SearchField__ViewMore" onClick={handleViewMore}>
              {"View more"}
            </a>
          ) : null
        }
      />
    </div>
  );
};

type SearchResultsItemProps = Parameters<
  NonNullable<React.ComponentProps<typeof Results>["renderResultCardProduct"]>
>[0];

function SearchResultsItem(props: SearchResultsItemProps) {
  const router = useRouter();
  const productUrl = `/products/${props.product.handle}`;

  const handleProductClick = (event: React.MouseEvent<HTMLAnchorElement>, product: ElasticProduct) => {
    event.preventDefault();
    props.handleClick();
    // @ts-expect-error unknown usage
    router.replace(productUrl, undefined, { product, type: "Search" });
  };

  return (
    <a
      ref={props.itemRef}
      target="_blank"
      href={productUrl}
      title={props.product.title}
      onClick={(event) => handleProductClick(event, props.product)}
      rel="noreferrer"
    >
      <Thumbnail
        source={
          props.product.image ||
          "data:image/png;base64,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"
        }
        alt={props.product.title}
        transparent={false}
      />
      <TextStyle>
        <TextStyle variation="strong">{props.product.title}</TextStyle>
        <br />${props.product.price_min}
      </TextStyle>
    </a>
  );
}
